import { z } from "zod"

/**
 * Per-device "show this track on the map" choice, as the set of track ids the
 * user has switched on. Tracks default to hidden, so a teammate's tracks never
 * clutter the map until someone opts in.
 *
 * Deliberately not a synced column: whether *you* want a track drawn says
 * nothing about whether a teammate does. Like the geospatial display prefs
 * (see geospatialDisplayPrefs/settings.ts) it is plain localStorage, with an
 * in-memory mirror so a write still sticks for the session when storage is
 * unavailable (private mode, quota).
 */
export const TRACK_DISPLAY_PREFS_STORAGE_KEY = "fieldbook.trackDisplayPrefs.v1"

const shownIdsSchema = z.array(z.string())

export const EMPTY_SHOWN_TRACK_IDS: readonly string[] = []

/** `undefined` until first read; thereafter the source of truth. */
let shownIds: readonly string[] | undefined

const listeners = new Set<() => void>()

function readFromStorage(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(TRACK_DISPLAY_PREFS_STORAGE_KEY)
    if (!raw) return EMPTY_SHOWN_TRACK_IDS
    const parsed = shownIdsSchema.safeParse(JSON.parse(raw))
    // Malformed local state should never crash the map — drop all of it.
    return parsed.success ? parsed.data : EMPTY_SHOWN_TRACK_IDS
  } catch {
    return EMPTY_SHOWN_TRACK_IDS
  }
}

/** Snapshot getter: the same array reference until something changes. */
export function getShownTrackIds(): readonly string[] {
  shownIds ??= readFromStorage()
  return shownIds
}

function notify() {
  for (const listener of listeners) listener()
}

function handleStorageEvent(event: StorageEvent) {
  if (event.key !== null && event.key !== TRACK_DISPLAY_PREFS_STORAGE_KEY) {
    return
  }
  shownIds = readFromStorage()
  notify()
}

/**
 * The `storage` event only fires in *other* tabs/webviews, so same-tab writes
 * notify their own subscribers explicitly. The listener is shared by every
 * subscriber: attached for the first, removed with the last.
 */
export function subscribeToTrackDisplayPrefs(onChange: () => void) {
  if (listeners.size === 0) {
    window.addEventListener("storage", handleStorageEvent)
  }
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
    if (listeners.size === 0) {
      window.removeEventListener("storage", handleStorageEvent)
    }
  }
}

export function setTrackShownOnMap(trackId: string, shown: boolean) {
  const current = getShownTrackIds()
  if (current.includes(trackId) === shown) return

  // Update the in-memory snapshot first and unconditionally: it is what makes
  // the change visible even when the write below fails.
  shownIds = shown
    ? [...current, trackId]
    : current.filter((id) => id !== trackId)
  try {
    window.localStorage.setItem(
      TRACK_DISPLAY_PREFS_STORAGE_KEY,
      JSON.stringify(shownIds),
    )
  } catch {
    // Storage unavailable — the in-memory snapshot is the fallback.
  }
  notify()
}

/** Test-only: the in-memory mirror outlives `localStorage.clear()`. */
export function __resetTrackDisplayPrefsCacheForTests() {
  shownIds = undefined
}
